import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
	MapPin,
	Phone,
	Mail,
	Clock,
	Instagram,
	Facebook,
	Send,
	ArrowRight,
} from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { supabase } from "../../lib/supabase";

export const Footer = () => {
	const currentYear = new Date().getFullYear();
	const [email, setEmail] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const handleNewsletterSubmit = async (e) => {
		e.preventDefault();

		if (!email.trim()) return;

		setIsLoading(true);

		try {
			const { data, error } = await supabase.functions.invoke(
				"confirm-newsletter-subscription",
				{
					body: { email: email.trim().toLowerCase() },
				},
			);

			if (error) throw error;

			if (data?.already) {
				toast.info("Vous êtes déjà inscrit à notre newsletter");
				return;
			}

			toast.success("Merci pour votre inscription !", {
				description: "Vous recevrez bientôt un email de confirmation.",
			});

			setEmail("");
		} catch (error) {
			console.error("Newsletter error:", error);
			toast.error("Une erreur est survenue");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<footer data-testid="footer" className="bg-espresso text-warm-alabaster">
			{/* Newsletter Section */}
			<div className="border-b border-warm-alabaster/10">
				<div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
						<div>
							<h3 className="font-serif text-2xl lg:text-3xl font-semibold mb-3">
								Restez Connecté
							</h3>
							<p className="text-warm-alabaster/70 leading-relaxed">
								Inscrivez-vous pour recevoir nos actualités, offres exclusives
								et
								<span className="text-latte-gold font-medium">
									{" "}
									10% de réduction
								</span>{" "}
								sur votre première commande.
							</p>
						</div>

						<div>
							<form onSubmit={handleNewsletterSubmit} className="flex gap-3">
								<Input
									type="email"
									placeholder="Votre adresse email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									required
									className="flex-1 h-14 bg-warm-alabaster/10 border-warm-alabaster/20 text-warm-alabaster placeholder:text-warm-alabaster/50 rounded-none focus:border-latte-gold"
									data-testid="footer-newsletter-email"
								/>

								<Button
									type="submit"
									disabled={isLoading}
									className="h-14 px-6 bg-latte-gold hover:bg-latte-gold-secondary text-espresso rounded-none"
									data-testid="footer-newsletter-submit"
								>
									{isLoading ? (
										<span className="spinner w-5 h-5" />
									) : (
										<Send className="w-5 h-5" />
									)}
								</Button>
							</form>
						</div>
					</div>
				</div>
			</div>

			{/* Main Footer */}
			<div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
					{/* Brand */}
					<div className="lg:col-span-1">
						<Link to="/" className="inline-block mb-6">
							<span className="font-serif text-2xl font-semibold text-warm-alabaster">
								Léger&apos;s <span className="text-latte-gold">Coffee</span>
							</span>
						</Link>

						<p className="text-warm-alabaster/70 text-sm leading-relaxed mb-6">
							Un café de spécialité au cœur de Genève. Savourez
							l&apos;excellence dans chaque tasse.
						</p>

						<div className="flex space-x-4">
							<a
								href="https://www.instagram.com/legerscoffee/"
								target="_blank"
								rel="noopener noreferrer"
								data-testid="social-instagram"
								className="w-10 h-10 rounded-full border border-warm-alabaster/20 flex items-center justify-center hover:border-latte-gold hover:text-latte-gold transition-colors duration-300"
								aria-label="Instagram"
							>
								<Instagram className="w-5 h-5" />
							</a>
						</div>
					</div>

					{/* Contact */}
					<div>
						<h4 className="font-serif text-lg font-semibold mb-6">Contact</h4>

						<ul className="space-y-4">
							<li className="flex items-start space-x-3">
								<MapPin className="w-5 h-5 text-latte-gold flex-shrink-0 mt-0.5" />
								<span className="text-warm-alabaster/70 text-sm">
									Pl. des Augustins 19
									<br />
									1205 Genève, Suisse
								</span>
							</li>

							<li className="flex items-center space-x-3">
								<Phone className="w-5 h-5 text-latte-gold flex-shrink-0" />
								<a
									href="tel:+41782215200"
									className="text-warm-alabaster/70 text-sm hover:text-latte-gold transition-colors duration-300"
								>
									Tel : +41 78 221 52 00
								</a>
							</li>

							<li className="flex items-center space-x-3">
								<Phone className="w-5 h-5 text-latte-gold flex-shrink-0" />
								<a
									href="tel:+41223135509"
									className="text-warm-alabaster/70 text-sm hover:text-latte-gold transition-colors duration-300"
								>
									Fixe : +41 22 313 55 09
								</a>
							</li>

							<li className="flex items-center space-x-3">
								<Mail className="w-5 h-5 text-latte-gold flex-shrink-0" />
								<a
									href="mailto:contact@legers-coffee.com"
									className="text-warm-alabaster/70 text-sm hover:text-latte-gold transition-colors duration-300"
								>
									contact@legers-coffee.com
								</a>
							</li>
						</ul>
					</div>

					{/* Hours */}
					<div>
						<h4 className="font-serif text-lg font-semibold mb-6">Horaires</h4>

						<ul className="space-y-3">
							<li className="flex items-start space-x-3">
								<Clock className="w-5 h-5 text-latte-gold flex-shrink-0 mt-0.5" />
								<div className="text-warm-alabaster/70 text-sm space-y-1">
									<p>Lun - Jeu : 07:00 – 20:30</p>
									<p>Vendredi : 07:00 – 17:00</p>
									<p>Samedi : 07:00 – 14:00</p>
									<p>Dimanche : Fermé</p>
								</div>
							</li>
						</ul>
					</div>

					{/* Navigation */}
					<div>
						<h4 className="font-serif text-lg font-semibold mb-6">
							Navigation
						</h4>

						<ul className="space-y-3">
							{[
								{ to: "/menu", label: "Notre Menu" },
								{ to: "/a-propos", label: "À Propos" },
								{ to: "/galerie", label: "Galerie" },
								{ to: "/reservation", label: "Réservation" },
								{ to: "/contact", label: "Contact" },
								{ to: "/newsletter", label: "Newsletter" },
							].map((link) => (
								<li key={link.to}>
									<Link
										to={link.to}
										className="text-warm-alabaster/70 text-sm hover:text-latte-gold transition-colors duration-300 flex items-center group"
									>
										{link.label}
										<ArrowRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
									</Link>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>

			{/* Bottom Bar */}
			<div className="border-t border-warm-alabaster/10">
				<div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
					<div className="flex flex-col md:flex-row items-center justify-between gap-4">
						<p className="text-warm-alabaster/50 text-xs text-center md:text-left">
							© {currentYear} Léger&apos;s Coffee. Tous droits réservés.
						</p>

						<div className="flex flex-wrap items-center justify-center gap-6">
							<Link
								to="/mentions-legales"
								className="text-warm-alabaster/50 text-xs hover:text-latte-gold transition-colors duration-300"
							>
								Mentions légales
							</Link>

							<Link
								to="/confidentialite"
								className="text-warm-alabaster/50 text-xs hover:text-latte-gold transition-colors duration-300"
							>
								Politique de confidentialité
							</Link>

							<button
								type="button"
								onClick={() =>
									window.dispatchEvent(new Event("open-cookie-preferences"))
								}
								className="text-warm-alabaster/50 text-xs hover:text-latte-gold transition-colors duration-300"
							>
								Gérer mes cookies
							</button>
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
